import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { STATUS_OPTIONS } from "../StatusChipSelector";
import { bulkUpdateRequests } from "../../api/requestsBulkApi";
import { useCompanies } from "../../hooks/useCompanies";
import { useChannels } from "../../hooks/useChannels";
import { useTypes } from "../../hooks/useTypes";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import { useClientTags } from "../../hooks/useClientTags";
import BulkActionConfirmStep from "./BulkActionConfirmStep";
import { buildBulkChangeSummary } from "./buildBulkChangeSummary";
import BulkFormImpactHint from "./BulkFormImpactHint";

const KEEP_VALUE = "";

function flattenTypesTree(nodes, parentId = null) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => {
    const id = Number(n.id);
    const idPai = n.id_pai != null && n.id_pai !== "" ? Number(n.id_pai) : parentId;
    const nome = n.nome ?? n.name ?? String(id);
    const { children: _c, ...rest } = n;
    return [{ ...rest, id, id_pai: idPai, nome }, ...flattenTypesTree(n.children || [], id)];
  });
}

function sameTypeId(a, b) {
  return a != null && a !== "" && b != null && b !== "" && String(a) === String(b);
}

function tagsToString(arr) {
  return Array.isArray(arr) && arr.length ? arr.join(", ") : "";
}

const EMPTY_FORM = {
  status: KEEP_VALUE,
  projectId: KEEP_VALUE,
  channelId: KEEP_VALUE,
  typeId: KEEP_VALUE,
  subtype1: KEEP_VALUE,
  subtype2: KEEP_VALUE,
  subtype3: KEEP_VALUE,
  assignee: KEEP_VALUE,
  potential: KEEP_VALUE,
  nextContactAt: "",
  nf: "",
  produto: "",
  campanha: "",
  textoAdicional: "",
};

function KeepMenuItem() {
  return (
    <MenuItem value={KEEP_VALUE}>
      <em>Manter valor atual</em>
    </MenuItem>
  );
}

export default function BulkChangeRequestsDialog({ open, selectedIds = [], onClose, onChanged }) {
  const ids = useMemo(
    () => [...new Set(selectedIds.map((id) => Number(id)).filter((n) => Number.isFinite(n) && n > 0))],
    [selectedIds]
  );

  const { data: companiesData, isLoading: loadingCompanies } = useCompanies(1, 200, "");
  const { data: channelsData, isLoading: loadingChannels } = useChannels(1, 200, "");
  const { data: typesData, isLoading: loadingTypes } = useTypes(1, 500, "");
  const { data: usersData, isLoading: loadingUsers } = useUsers(1, 1000, "", "todos", {
    order: "nome",
  });
  const { data: agentsData, isLoading: loadingAgents } = useAgents(1, 200, "");
  const { data: tagOptions } = useClientTags({ search: "", limit: 200, includeUsed: true });

  const companies = companiesData?.companies || [];
  const channels = channelsData?.channels || [];
  const typesFlat = useMemo(() => flattenTypesTree(typesData?.types ?? []), [typesData?.types]);
  const rootTypes = useMemo(() => typesFlat.filter((t) => !t.id_pai), [typesFlat]);
  const usersList = usersData?.users || [];
  const agentsList = agentsData?.agents || [];
  const allowedTagNames = (tagOptions || [])
    .filter((t) => (t?.status == null ? true : Number(t.status) === 1))
    .map((t) => String(t?.nome || "").trim())
    .filter(Boolean);

  const [form, setForm] = useState(EMPTY_FORM);
  const [replaceTags, setReplaceTags] = useState(false);
  const [tags, setTags] = useState([]);
  const [step, setStep] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const subtype1Options = useMemo(
    () => (form.typeId ? typesFlat.filter((t) => sameTypeId(t.id_pai, form.typeId)) : []),
    [form.typeId, typesFlat]
  );
  const subtype2Options = useMemo(
    () => (form.subtype1 ? typesFlat.filter((t) => sameTypeId(t.id_pai, form.subtype1)) : []),
    [form.subtype1, typesFlat]
  );
  const subtype3Options = useMemo(
    () => (form.subtype2 ? typesFlat.filter((t) => sameTypeId(t.id_pai, form.subtype2)) : []),
    [form.subtype2, typesFlat]
  );

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setReplaceTags(false);
    setTags([]);
    setStep("form");
    setError(null);
  }, [open]);

  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "typeId") {
        next.subtype1 = KEEP_VALUE;
        next.subtype2 = KEEP_VALUE;
        next.subtype3 = KEEP_VALUE;
      } else if (field === "subtype1") {
        next.subtype2 = KEEP_VALUE;
        next.subtype3 = KEEP_VALUE;
      } else if (field === "subtype2") {
        next.subtype3 = KEEP_VALUE;
      }
      return next;
    });
  };

  const hasChanges = useMemo(() => {
    const baseChanged = Object.entries(form).some(([key, value]) => {
      if (key === "nextContactAt" || key === "nf" || key === "produto" || key === "campanha" || key === "textoAdicional") {
        return String(value || "").trim() !== "";
      }
      return value !== KEEP_VALUE;
    });
    return baseChanged || replaceTags;
  }, [form, replaceTags]);

  const loadingMeta = loadingCompanies || loadingChannels || loadingTypes || loadingUsers || loadingAgents;

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    setStep("form");
    onClose?.();
  };

  const confirmSummary = useMemo(
    () =>
      buildBulkChangeSummary({
        form,
        replaceTags,
        tags,
        companies,
        channels,
        typesFlat,
        usersList,
        agentsList,
      }),
    [form, replaceTags, tags, companies, channels, typesFlat, usersList, agentsList]
  );

  const validateForm = () => {
    if (!ids.length) {
      setError("Selecione pelo menos 1 solicitação.");
      return false;
    }
    if (!hasChanges) {
      setError("Informe pelo menos um campo para alterar.");
      return false;
    }
    if (form.status === "3" && !form.nextContactAt.trim()) {
      setError("Informe a data do próximo contato para o status Contato Futuro.");
      return false;
    }
    setError(null);
    return true;
  };

  const handleGoToConfirm = () => {
    if (!validateForm()) return;
    setStep("confirm");
  };

  const buildPatch = () => {
    const patch = {};
    if (form.status !== KEEP_VALUE) patch.status = Number(form.status);
    if (form.projectId !== KEEP_VALUE) patch.id_projeto = Number(form.projectId);
    if (form.channelId !== KEEP_VALUE) patch.id_origem = Number(form.channelId);
    if (form.typeId !== KEEP_VALUE) patch.id_tipo = Number(form.typeId);
    if (form.subtype1 !== KEEP_VALUE) patch.id_subtipo1 = Number(form.subtype1);
    if (form.subtype2 !== KEEP_VALUE) patch.id_subtipo2 = Number(form.subtype2);
    if (form.subtype3 !== KEEP_VALUE) patch.id_subtipo3 = Number(form.subtype3);
    if (form.assignee !== KEEP_VALUE) patch.assignee = form.assignee;
    if (form.potential !== KEEP_VALUE) patch.potencial = Number(form.potential);
    if (form.status === "3" && form.nextContactAt.trim()) {
      patch.dataproximocontato = form.nextContactAt.trim();
    }
    if (form.nf.trim()) patch.nf = form.nf.trim();
    if (form.produto.trim()) patch.produto = form.produto.trim();
    if (form.campanha.trim()) patch.campanha = form.campanha.trim();
    if (form.textoAdicional.trim()) patch.texto_adicional = form.textoAdicional.trim();
    if (replaceTags) patch.tags = tagsToString(tags);
    return patch;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    setError(null);
    try {
      await bulkUpdateRequests(ids, buildPatch());
      onChanged?.();
      handleClose();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Erro ao alterar solicitações.");
      setStep("confirm");
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "confirm") {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Revisar alteração em massa</DialogTitle>
        {error && (
          <Box px={3} pt={1}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        <BulkActionConfirmStep
          ids={ids}
          actionTitle="Revisar alteração em massa"
          impactVerb="alteradas"
          irreversible
          changeLines={confirmSummary.lines}
          warnings={confirmSummary.warnings}
          onBack={() => {
            setError(null);
            setStep("form");
          }}
          onConfirm={handleSubmit}
          submitting={submitting}
          confirmButtonLabel="Confirmar alterações"
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Alterar detalhes das solicitações</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        <Stack spacing={2}>
          <BulkFormImpactHint count={ids.length} impactVerb="alteradas" irreversible />

          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth size="small" disabled={submitting}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={form.status} onChange={(e) => setField("status", e.target.value)}>
              <KeepMenuItem />
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {form.status === "3" && (
            <TextField
              fullWidth
              size="small"
              label="Data e hora do próximo contato"
              type="datetime-local"
              value={form.nextContactAt}
              onChange={(e) => setField("nextContactAt", e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={submitting}
            />
          )}

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={replaceTags}
                  onChange={(e) => setReplaceTags(e.target.checked)}
                  disabled={submitting}
                />
              }
              label="Substituir tags"
            />
            {replaceTags && (
              <Autocomplete
                multiple
                options={allowedTagNames}
                value={tags}
                onChange={(_e, value) => setTags(value)}
                disabled={submitting}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Selecione as tags"
                    helperText="Substitui as tags atuais em todas as solicitações selecionadas."
                  />
                )}
              />
            )}
          </Box>

          <FormControl fullWidth size="small" disabled={submitting || loadingCompanies}>
            <InputLabel>Empresa</InputLabel>
            <Select label="Empresa" value={form.projectId} onChange={(e) => setField("projectId", e.target.value)}>
              <KeepMenuItem />
              {companies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.nome || c.name || `Empresa ${c.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={submitting || loadingChannels}>
            <InputLabel>Canal</InputLabel>
            <Select label="Canal" value={form.channelId} onChange={(e) => setField("channelId", e.target.value)}>
              <KeepMenuItem />
              {channels.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.nome || c.name || `Canal ${c.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={submitting || loadingTypes}>
            <InputLabel>Tipo</InputLabel>
            <Select label="Tipo" value={form.typeId} onChange={(e) => setField("typeId", e.target.value)}>
              <KeepMenuItem />
              {rootTypes.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {subtype1Options.length > 0 && (
            <FormControl fullWidth size="small" disabled={submitting}>
              <InputLabel>Subtipo 1</InputLabel>
              <Select label="Subtipo 1" value={form.subtype1} onChange={(e) => setField("subtype1", e.target.value)}>
                <KeepMenuItem />
                {subtype1Options.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {subtype2Options.length > 0 && (
            <FormControl fullWidth size="small" disabled={submitting}>
              <InputLabel>Subtipo 2</InputLabel>
              <Select label="Subtipo 2" value={form.subtype2} onChange={(e) => setField("subtype2", e.target.value)}>
                <KeepMenuItem />
                {subtype2Options.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {subtype3Options.length > 0 && (
            <FormControl fullWidth size="small" disabled={submitting}>
              <InputLabel>Subtipo 3</InputLabel>
              <Select label="Subtipo 3" value={form.subtype3} onChange={(e) => setField("subtype3", e.target.value)}>
                <KeepMenuItem />
                {subtype3Options.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth size="small" disabled={submitting || loadingUsers || loadingAgents}>
            <InputLabel>Responsável</InputLabel>
            <Select label="Responsável" value={form.assignee} onChange={(e) => setField("assignee", e.target.value)}>
              <KeepMenuItem />
              <MenuItem value="queue">Fila (sem atribuição)</MenuItem>
              {agentsList.length > 0 && (
                <MenuItem disabled sx={{ fontWeight: 600, opacity: 0.85 }}>
                  Agentes de IA
                </MenuItem>
              )}
              {agentsList.map((a) => {
                const id = a.id ?? a.id_agente ?? a.agentId;
                return (
                  <MenuItem key={`agent-${id}`} value={`agent:${id}`}>
                    {a.name ?? a.nome ?? `Agente ${id}`}
                  </MenuItem>
                );
              })}
              {usersList.length > 0 && (
                <MenuItem disabled sx={{ fontWeight: 600, opacity: 0.85 }}>
                  Usuários
                </MenuItem>
              )}
              {usersList.map((u) => (
                <MenuItem key={`user-${u.id}`} value={`user:${u.id}`}>
                  {u.nome ?? u.name ?? `Usuário ${u.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={submitting}>
            <InputLabel>Potencial</InputLabel>
            <Select label="Potencial" value={form.potential} onChange={(e) => setField("potential", e.target.value)}>
              <KeepMenuItem />
              <MenuItem value="1">Baixo</MenuItem>
              <MenuItem value="2">Médio</MenuItem>
              <MenuItem value="3">Alto</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label="NF"
            value={form.nf}
            onChange={(e) => setField("nf", e.target.value)}
            disabled={submitting}
            placeholder="Deixe vazio para não alterar"
          />

          <TextField
            fullWidth
            size="small"
            label="Produto"
            value={form.produto}
            onChange={(e) => setField("produto", e.target.value)}
            disabled={submitting}
            placeholder="Deixe vazio para não alterar"
          />

          <TextField
            fullWidth
            size="small"
            label="Campanha"
            value={form.campanha}
            onChange={(e) => setField("campanha", e.target.value)}
            disabled={submitting}
            placeholder="Deixe vazio para não alterar"
          />

          <TextField
            fullWidth
            size="small"
            label="Campo adicional"
            value={form.textoAdicional}
            onChange={(e) => setField("textoAdicional", e.target.value)}
            disabled={submitting}
            placeholder="Deixe vazio para não alterar"
          />

          {(loadingMeta || submitting) && (
            <Box display="flex" justifyContent="center" py={1}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleGoToConfirm} disabled={submitting || !ids.length || !hasChanges}>
          Revisar alterações
        </Button>
      </DialogActions>
    </Dialog>
  );
}
